import React, { useState, useEffect } from 'react';
import { Save, Eye, FileText, Shield, Settings, Printer } from 'lucide-react';
import api from '../services/api';

const SECTIONS = [
  { id: 'attorney', label: 'Attorney Info', icon: Shield },
  { id: 'formatting', label: 'Document Formatting', icon: FileText },
  { id: 'eportal', label: 'ePortal Credentials', icon: Settings },
  { id: 'rules', label: 'Submission Rules', icon: Printer },
  { id: 'phone', label: 'Phone Routing', icon: Settings },
];

const FirmFormattingSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('attorney');

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get('/firm/formatting');
        setSettings(resp.data || {});
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/firm/formatting', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading) return <div className="p-8 text-gray-500 animate-pulse">Loading settings...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-200">Firm Formatting Settings</h1>
        <button onClick={handleSave} disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition ${saved ? 'bg-green-700 text-white' : 'bg-teal-700 hover:bg-teal-600 text-white'}`}>
          <Save className="w-4 h-4" /> {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Form */}
        <div className="col-span-2 space-y-4">
          {/* Section tabs */}
          <div className="flex gap-1 border-b border-gray-700 pb-2">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              return (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition ${activeSection === s.id ? 'border-teal-400 text-teal-300' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </div>

          {activeSection === 'attorney' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Attorney Name" value={settings.attorney_name} onChange={v => update('attorney_name', v)} />
              <Field label="Title" value={settings.attorney_title} onChange={v => update('attorney_title', v)} />
              <Field label="FL Bar Number" value={settings.bar_number_fl} onChange={v => update('bar_number_fl', v)} />
              <Field label="GA Bar Number" value={settings.bar_number_ga} onChange={v => update('bar_number_ga', v)} />
              <Field label="Firm Name" value={settings.firm_name} onChange={v => update('firm_name', v)} />
              <Field label="E-Signature Line" value={settings.esignature_line} onChange={v => update('esignature_line', v)} />
              <Field label="Address Line 1" value={settings.firm_address_line1} onChange={v => update('firm_address_line1', v)} />
              <Field label="Address Line 2" value={settings.firm_address_line2} onChange={v => update('firm_address_line2', v)} />
              <Field label="Phone" value={settings.firm_phone} onChange={v => update('firm_phone', v)} />
              <Field label="Email" value={settings.firm_email} onChange={v => update('firm_email', v)} />
            </div>
          )}

          {activeSection === 'formatting' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Font Family" value={settings.font_family} onChange={v => update('font_family', v)} />
              <Field label="Font Size (pt)" value={settings.font_size_pt} onChange={v => update('font_size_pt', parseInt(v) || 12)} type="number" />
              <SelectField label="Line Spacing" value={settings.line_spacing} onChange={v => update('line_spacing', v)}
                options={[{v: 'single', l: 'Single'}, {v: 'double', l: 'Double'}, {v: '1.5', l: '1.5'}]} />
              <SelectField label="Caption Format" value={settings.caption_format} onChange={v => update('caption_format', v)}
                options={[{v: 'standard_fl', l: 'Florida Standard'}, {v: 'standard_ga', l: 'Georgia Standard'}, {v: 'federal', l: 'Federal'}]} />
              <Field label="Top Margin (in)" value={settings.margin_top_in} onChange={v => update('margin_top_in', parseFloat(v) || 1.0)} type="number" step="0.25" />
              <Field label="Bottom Margin (in)" value={settings.margin_bottom_in} onChange={v => update('margin_bottom_in', parseFloat(v) || 1.0)} type="number" step="0.25" />
              <Field label="Left Margin (in)" value={settings.margin_left_in} onChange={v => update('margin_left_in', parseFloat(v) || 1.25)} type="number" step="0.25" />
              <Field label="Right Margin (in)" value={settings.margin_right_in} onChange={v => update('margin_right_in', parseFloat(v) || 1.25)} type="number" step="0.25" />
              <div className="col-span-2">
                <TextArea label="Certificate of Service Template" value={settings.certificate_of_service_template}
                  onChange={v => update('certificate_of_service_template', v)} rows={6} />
              </div>
              <div className="col-span-2">
                <TextArea label="Signature Block Template" value={settings.filing_signature_block_template}
                  onChange={v => update('filing_signature_block_template', v)} rows={5} />
              </div>
            </div>
          )}

          {activeSection === 'eportal' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="ePortal Username" value={settings.eportal_username} onChange={v => update('eportal_username', v)} />
              <Field label="ePortal Password" value={settings.eportal_password} onChange={v => update('eportal_password', v)} type="password"
                placeholder={settings.eportal_password_set ? '•••••••• (set — type to replace)' : 'Not set'} />
              <Field label="Default Payment Method" value={settings.eportal_default_payment_method}
                onChange={v => update('eportal_default_payment_method', v)} placeholder="e.g., Firm Credit Card" />
            </div>
          )}

          {activeSection === 'phone' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Transfer Number (Office Manager)" value={settings.kaleigh_transfer_number}
                onChange={v => update('kaleigh_transfer_number', v)} placeholder="e.g., +19045551234" />
              <Field label="Name" value={settings.kaleigh_name}
                onChange={v => update('kaleigh_name', v)} />
              <Field label="Business Hours Start" value={settings.business_hours_start}
                onChange={v => update('business_hours_start', v)} type="time" />
              <Field label="Business Hours End" value={settings.business_hours_end}
                onChange={v => update('business_hours_end', v)} type="time" />
              <SelectField label="Timezone" value={settings.business_hours_timezone}
                onChange={v => update('business_hours_timezone', v)}
                options={[
                  {v: 'America/New_York', l: 'Eastern'},
                  {v: 'America/Chicago', l: 'Central'},
                  {v: 'America/Denver', l: 'Mountain'},
                  {v: 'America/Los_Angeles', l: 'Pacific'},
                ]} />
              <Field label="Voicemail Number" value={settings.voicemail_number}
                onChange={v => update('voicemail_number', v)} placeholder="Optional fallback" />
              <div className="col-span-2 bg-gray-800/50 rounded p-3 border border-gray-700 text-xs text-gray-400 space-y-1">
                <p><strong>Call routing logic:</strong></p>
                <p>• Known clients with active cases → PIN verification → case status / billing / transfer</p>
                <p>• Emergencies → immediate transfer to {settings.kaleigh_name || 'Office Manager'} + SMS/email alert</p>
                <p>• Complaints → empathy response → transfer + retention action created</p>
                <p>• Opposing counsel → transfer during hours, message after hours</p>
                <p>• Unknown callers → intake pipeline</p>
                <p>• Outside business hours → voicemail with transcript + email notification</p>
              </div>
            </div>
          )}

          {activeSection === 'rules' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Auto-Submit Confidence Threshold" value={settings.auto_submit_threshold}
                onChange={v => update('auto_submit_threshold', parseFloat(v) || 0.95)} type="number" step="0.05" />
              <div className="flex items-center gap-3 bg-gray-800/50 rounded p-3 border border-gray-700">
                <input type="checkbox" checked={settings.require_attorney_review ?? true}
                  onChange={e => update('require_attorney_review', e.target.checked)}
                  className="w-4 h-4 rounded" />
                <div>
                  <span className="text-sm text-gray-200">Require Attorney Review</span>
                  <p className="text-[10px] text-gray-500">When enabled, all filings must be manually approved before submission</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Preview */}
        <div className="bg-gray-900/60 rounded-xl border border-gray-700 p-4 overflow-auto max-h-[700px]">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Preview
          </h3>
          <div className="bg-white text-black p-6 rounded font-serif text-[11px] leading-relaxed" style={{ fontFamily: settings.font_family || 'Times New Roman' }}>
            <div className="text-center text-xs uppercase tracking-wide mb-4">
              IN THE CIRCUIT COURT OF THE<br/>FOURTH JUDICIAL CIRCUIT<br/>IN AND FOR NASSAU COUNTY, FLORIDA
            </div>
            <div className="flex justify-between border-t border-b border-black py-2 mb-2">
              <div className="w-1/2">
                JOHN DOE,<br/><span className="ml-4">Plaintiff,</span><br/>v.<br/>JANE SMITH,<br/><span className="ml-4">Defendant.</span>
              </div>
              <div className="w-1/2 text-right text-xs">
                Case No. 2025-CA-000123<br/>Division: A<br/>Judge: Hon. Robert Foster
              </div>
            </div>
            <div className="text-center font-bold uppercase mb-4">RESPONSE IN OPPOSITION TO MOTION TO DISMISS</div>
            <p>COMES NOW the Plaintiff, JOHN DOE, by and through undersigned counsel, and hereby responds to Defendant's Motion to Dismiss...</p>
            <div className="mt-6 text-xs">
              <p>Respectfully submitted,</p>
              <p className="mt-4 italic">{settings.esignature_line || ''}</p>
              <p>{settings.attorney_name || ''}, {settings.attorney_title || 'Esq.'}</p>
              <p>{settings.firm_name || ''}</p>
              <p>{settings.firm_address_line1 || 'P.O. Box 130'}</p>
              <p>{settings.firm_address_line2 || ''}</p>
              <p>{settings.firm_phone || ''}</p>
              <p>{settings.firm_email || ''}</p>
              <p>Florida Bar No. {settings.bar_number_fl || '1002420'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder = '', step }) => (
  <div>
    <label className="text-xs text-gray-500 block mb-1">{label}</label>
    <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} step={step}
      className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-teal-500" />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs text-gray-500 block mb-1">{label}</label>
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-teal-500">
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const TextArea = ({ label, value, onChange, rows = 4 }) => (
  <div>
    <label className="text-xs text-gray-500 block mb-1">{label}</label>
    <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows}
      className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm text-gray-200 font-mono resize-y focus:outline-none focus:border-teal-500" />
  </div>
);

export default FirmFormattingSettings;
